import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, CheckCircle2, Clock, Trophy, Users } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { useCountdown } from '@/hooks/useCountdown';
import { useLocalized } from '@/hooks/useLocalized';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { formatDate } from '@/lib/format';
import { contestsApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import { mapDifficulty } from '@/api/mappers';
import type {
  ContestDetailResponse,
  ContestMeResponse,
  ContestProblemResponse,
  ContestStandingCellDto,
  ContestStandingResponse,
} from '@/api/types';
import { NotFound } from './NotFound';
import { useSeo } from '@/hooks/useSeo';

type Tab = 'problems' | 'standings';

export function Contest() {
  // Shaxsiy/JS-only sahifa: indekslanmaydi; oldingi sahifaning JSON-LD i tozalanadi
  useSeo({ title: "Musobaqa — SQLMaster.uz", noindex: true });
  const { slug = '' } = useParams();
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const push = useToastStore((s) => s.push);

  const [contest, setContest] = useState<ContestDetailResponse | null>(null);
  const [problems, setProblems] = useState<ContestProblemResponse[] | null>(null);
  const [problemsForbidden, setProblemsForbidden] = useState(false);
  const [me, setMe] = useState<ContestMeResponse | null>(null);
  const [standings, setStandings] = useState<ContestStandingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>('problems');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    contestsApi
      .detail(slug, lang)
      .then(async (res) => {
        setContest(res.data);
        const c = res.data;

        const standingsP = contestsApi.standings(slug, 0, 50).then((r) => setStandings(r.data.content));

        let meP: Promise<void> = Promise.resolve();
        if (isAuthenticated) {
          meP = contestsApi
            .me(slug)
            .then((r) => setMe(r.data))
            .catch(() => setMe(null));
        } else {
          setMe(null);
        }

        // Masalalar: faqat ruxsat bo'lsa
        let problemsP: Promise<void> = Promise.resolve();
        const canTryProblems =
          c.state === 'FINISHED' ||
          (c.state === 'LIVE' && (c.registered || isAuthenticated)) ||
          c.state === 'UPCOMING'; // urinish → 403 bo'lishi mumkin

        if (canTryProblems && c.state !== 'UPCOMING') {
          problemsP = contestsApi
            .problems(slug, lang)
            .then((r) => {
              setProblems(r.data);
              setProblemsForbidden(false);
            })
            .catch((err) => {
              const status = (err as { response?: { status?: number } })?.response?.status;
              if (status === 403) {
                setProblems(null);
                setProblemsForbidden(true);
              } else {
                setProblems(null);
              }
            });
        } else {
          setProblems(null);
          setProblemsForbidden(c.state === 'UPCOMING' || (c.state === 'LIVE' && !c.registered));
        }

        await Promise.all([standingsP, meP, problemsP]);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const countdownTarget =
    contest?.state === 'LIVE' ? contest.endsAt : contest?.startsAt ?? '';
  const cd = useCountdown(countdownTarget || new Date(0).toISOString());

  useEffect(() => {
    if (cd.done && contest && contest.state !== 'FINISHED') {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cd.done]);

  const handleRegister = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/contests/${slug}`);
      return;
    }
    setBusy(true);
    try {
      await contestsApi.register(slug);
      push(t('toast.contestRegistered'), 'success');
      load();
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-8xl px-4 py-8">
        <div className="h-40 animate-pulse rounded-lg bg-surface" />
      </div>
    );
  }

  if (notFound || !contest) {
    return <NotFound />;
  }

  const registered = me?.registered ?? contest.registered;
  const statusBySlug = new Map((me?.problems ?? []).map((p) => [p.problemSlug, p]));

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <Link
        to="/contests"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-content-secondary hover:text-content"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('contests.title')}
      </Link>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold sm:text-3xl">{contest.title}</h1>
            <StateBadge state={contest.state} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-content-muted">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(contest.startsAt, lang)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {t('contests.duration', { min: contest.durationMin })}
            </span>
            {contest.problemCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Trophy className="h-4 w-4" />
                {t('contests.problemsN', { count: contest.problemCount })}
              </span>
            )}
            {contest.participantCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {t('contests.participants', { count: contest.participantCount })}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 sm:items-end">
          {contest.state !== 'FINISHED' && !cd.done && (
            <div className="flex gap-2 text-center">
              {[
                { v: cd.days, u: t('contests.days') },
                { v: cd.hours, u: t('contests.hours') },
                { v: cd.minutes, u: t('contests.minutes') },
                { v: cd.seconds, u: 's' },
              ].map((seg, i) => (
                <div key={i} className="rounded-DEFAULT bg-surface px-2.5 py-1.5">
                  <div className="tnum text-lg font-bold leading-none">
                    {String(seg.v).padStart(2, '0')}
                  </div>
                  <div className="mt-0.5 text-[10px] uppercase text-content-muted">{seg.u}</div>
                </div>
              ))}
            </div>
          )}
          {(contest.state === 'UPCOMING' || contest.state === 'LIVE') && (
            <Button
              variant={registered ? 'secondary' : 'primary'}
              size="sm"
              disabled={registered || busy}
              loading={busy}
              onClick={handleRegister}
            >
              {registered ? t('contests.registered') : t('contests.register')}
            </Button>
          )}
        </div>
      </div>

      {contest.descriptionMd && (
        <Card className="mb-6 p-5">
          <MarkdownLite text={contest.descriptionMd} />
        </Card>
      )}

      {registered && me && contest.state !== 'UPCOMING' && (
        <Card className="mb-6 flex flex-wrap gap-6 p-4 text-sm">
          {me.rank != null && (
            <div>
              <div className="text-content-muted">{t('contests.colRank')}</div>
              <div className="tnum text-lg font-semibold">{me.rank}</div>
            </div>
          )}
          <div>
            <div className="text-content-muted">{t('contests.colScore')}</div>
            <div className="tnum text-lg font-semibold">{me.score}</div>
          </div>
          <div>
            <div className="text-content-muted">{t('contests.colPenalty')}</div>
            <div className="tnum text-lg font-semibold">{formatPenalty(me.penaltySec)}</div>
          </div>
        </Card>
      )}

      <div className="mb-4 flex gap-2 border-b border-line">
        {(['problems', 'standings'] as Tab[]).map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={
              tab === tb
                ? 'border-b-2 border-brand-600 px-3 py-2 text-sm font-medium text-brand-600'
                : 'px-3 py-2 text-sm text-content-muted hover:text-content'
            }
          >
            {tb === 'problems' ? t('contests.tabProblems') : t('contests.tabStandings')}
          </button>
        ))}
      </div>

      {tab === 'problems' && (
        <>
          {contest.state === 'UPCOMING' && (
            <Card className="p-6 text-center text-content-secondary">
              {t('contests.problemsHiddenUpcoming')}
            </Card>
          )}
          {contest.state === 'LIVE' && !registered && (
            <Card className="p-6 text-center">
              <p className="text-content-secondary">{t('contests.notRegisteredLive')}</p>
              <Button className="mt-4" size="sm" loading={busy} onClick={handleRegister}>
                {t('contests.register')}
              </Button>
            </Card>
          )}
          {problemsForbidden && contest.state !== 'UPCOMING' && registered === false && contest.state !== 'LIVE' && null}
          {problems && problems.length > 0 && (
            <div className="space-y-2">
              {problems.map((p) => {
                const st = statusBySlug.get(p.problemSlug);
                const href =
                  contest.state === 'LIVE'
                    ? `/contests/${slug}/problems/${p.problemSlug}`
                    : `/problems/${p.problemSlug}`;
                return (
                  <Link key={p.problemSlug} to={href}>
                    <Card className="flex flex-wrap items-center justify-between gap-3 p-4 transition hover:border-brand-500/40">
                      <div className="flex items-center gap-3">
                        <span className="tnum w-8 text-content-muted">{p.sortOrder}</span>
                        <div>
                          <div className="font-medium">{p.title}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-content-muted">
                            <DifficultyBadge difficulty={mapDifficulty(p.difficulty)} />
                            <span>
                              {t('contests.points', { n: p.points })}
                            </span>
                          </div>
                        </div>
                      </div>
                      {st && (
                        <div className="flex items-center gap-2 text-sm">
                          {st.solved ? (
                            <span className="inline-flex items-center gap-1 text-[var(--success)]">
                              <CheckCircle2 className="h-4 w-4" />
                              {t('status.solved')}
                            </span>
                          ) : st.attempts > 0 ? (
                            <span className="text-content-muted">
                              {t('contests.attempts', { n: st.attempts })}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
          {problems && problems.length === 0 && contest.state === 'FINISHED' && (
            <Card className="p-6 text-center text-content-muted">{t('contests.noProblems')}</Card>
          )}
        </>
      )}

      {tab === 'standings' && (
        <Card className="overflow-hidden">
          {standings.length === 0 ? (
            <p className="p-6 text-center text-content-muted">{t('contests.emptyStandings')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase text-content-muted">
                      <th className="px-4 py-3">{t('contests.colRank')}</th>
                      <th className="px-4 py-3">{t('contests.colUser')}</th>
                      <th className="px-4 py-3 text-right">{t('contests.colScore')}</th>
                      <th className="px-4 py-3 text-right">{t('contests.colPenalty')}</th>
                      {/* Ustun sarlavhalari birinchi qatordan — har qatorda bir xil tartib */}
                      {standings[0].cells.map((c) => (
                        <th
                          key={c.sortOrder}
                          className="px-2 py-3 text-center"
                          title={t('contests.pointsN', { count: c.points })}
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((row) => (
                      <tr key={row.userId} className="border-b border-line/60 hover:bg-surface">
                        <td className="tnum px-4 py-2.5 font-medium">{row.rank}</td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium">{row.displayName || row.username}</div>
                          <div className="text-xs text-content-muted">@{row.username}</div>
                        </td>
                        <td className="tnum px-4 py-2.5 text-right font-semibold">{row.score}</td>
                        <td className="tnum px-4 py-2.5 text-right text-content-secondary">
                          {formatPenalty(row.penaltySec)}
                        </td>
                        {row.cells.map((c) => (
                          <StandingCell key={c.sortOrder} cell={c} />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-line px-4 py-3 text-xs text-content-muted">
                <span>
                  <span className="font-mono font-semibold text-[var(--easy)]">+N</span>{' '}
                  {t('contests.legendSolved')}
                </span>
                <span>
                  <span className="font-mono font-semibold text-[var(--error)]">−N</span>{' '}
                  {t('contests.legendFailed')}
                </span>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  const { t } = useTranslation();
  if (state === 'LIVE') {
    return (
      <span className="rounded bg-[var(--error)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--error)]">
        {t('contests.live')}
      </span>
    );
  }
  if (state === 'FINISHED') {
    return (
      <span className="rounded bg-surface px-2 py-0.5 text-xs font-medium text-content-secondary">
        {t('contests.finished')}
      </span>
    );
  }
  return (
    <span className="rounded bg-brand-500/15 px-2 py-0.5 text-xs font-medium text-brand-600">
      {t('contests.upcoming')}
    </span>
  );
}

function formatPenalty(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ICPC uslubidagi hujayra:
 *   yechilgan      → +N va ostida yechish vaqti
 *   urinilgan      → −N
 *   tegilmagan     → bo'sh
 */
function StandingCell({ cell }: { cell: ContestStandingCellDto }) {
  if (cell.solved) {
    return (
      <td className="px-2 py-2 text-center align-middle bg-[var(--easy)]/10">
        <div className="tnum font-mono text-sm font-semibold text-[var(--easy)]">
          +{cell.attempts}
        </div>
        {cell.solveTimeSec !== null && (
          <div className="tnum font-mono text-[11px] leading-tight text-content-muted">
            {formatPenalty(cell.solveTimeSec)}
          </div>
        )}
      </td>
    );
  }

  if (cell.attempts > 0) {
    return (
      <td className="px-2 py-2 text-center align-middle bg-[var(--error)]/10">
        <div className="tnum font-mono text-sm font-semibold text-[var(--error)]">
          −{cell.attempts}
        </div>
      </td>
    );
  }

  return <td className="px-2 py-2 text-center align-middle text-content-muted">·</td>;
}
